import { ButcherHeader } from '@components/ButcherHeader';
import { OrderNotesModal } from '@components/Modal/OrderProcessing/OrderNotesModal';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import {
  errorMessage,
  Length,
  OrderProcessingMasterData,
  OrderProcessStatus,
  responseTexts,
  ServiceProcessingCrumbSteps,
} from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { ServiceOrderDetails, ServiceTaskDetails } from '@helpers/Models';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import React from 'react';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { faCircleCheck, faPen, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ServiceDraft {
  measurement: number;
  note: string;
  noteId?: number;
}

export const ServiceProcessTaskDetails: React.FC = () => {
  const { showToastMessage } = useToast();
  const { state } = useLocation();
  // Getting species id from the previous page - service process task list
  const orderSpeciesId = state?.orderSpeciesId;

  const [orderNumber, setOrderNumber] = useState<string>('');
  const [orderDetails, setOrderDetails] = useState<ServiceOrderDetails>();
  const [speciesName, setSpeciesName] = useState<string>('');

  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [serviceDrafts, setServiceDrafts] = useState<
    Record<number, ServiceDraft>
  >({});
  const [directCompleteServiceId, setDirectCompleteServiceId] = useState<
    number | null
  >(null);
  const [editingServiceId, setEditingServiceId] = useState<number | null>(null);

  useEffect(() => {
    fetchServiceTaskDetails();
  }, [orderSpeciesId]);

  /**
   * Fetches detailed information about a specific order using its ID.
   *
   * Makes a POST request to the `ORDER_DETAILS` API endpoint with the provided `orderId`,
   * retrieves the order data, and updates local state variables:
   * - `orderNumber` (order identifier),
   * - `orderDetails` (full order data),
   * - `estCompletionDate` (parsed estimated completion date).
   *
   * Displays a toast message if the request fails.
   *
   * @async
   * @function fetchServiceTaskDetails
   * @returns {Promise<void>} A Promise that resolves when the data is successfully fetched and state is updated.
   * @throws Will throw an error if the API call fails.
   */
  const fetchServiceTaskDetails = async (): Promise<void> => {
    try {
      const response = await Axios.post(
        ApiEndpoints.SERVICE_PROCESS_TASK_DETAILS,
        {
          orderSpeciesId: orderSpeciesId,
        },
      );
      const orderDetails = response.data.data;
      setOrderNumber(orderDetails[0].orderNumber);
      setOrderDetails(orderDetails[0]);
      setSpeciesName(orderDetails[0].speciesTag);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const buildDefaultDraft = (service: ServiceTaskDetails): ServiceDraft => ({
    measurement: service.serviceProcessDetail?.finalMeasurement ?? 0,
    note: service.serviceNotes?.[0]?.note ?? '',
    noteId: service.serviceNotes?.[0]?.id,
  });

  const ensureDraft = (service: ServiceTaskDetails): void => {
    setServiceDrafts((prev) => {
      if (prev[service.orderServiceId]) {
        return prev;
      }

      return {
        ...prev,
        [service.orderServiceId]: buildDefaultDraft(service),
      };
    });
  };

  const updateDraft = (
    serviceId: number,
    values: Partial<ServiceDraft>,
  ): void => {
    setServiceDrafts((prev) => {
      const existing = prev[serviceId] ?? { measurement: 0, note: '' };
      return {
        ...prev,
        [serviceId]: {
          ...existing,
          ...values,
        },
      };
    });
  };

  const handleStartProcessing = async (service: ServiceTaskDetails) => {
    try {
      await Axios.post(ApiEndpoints.SERVICE_PROCESS_WORKFLOW, {
        orderServiceId: service.orderServiceId,
        status: OrderProcessStatus.INPROGRESS,
      });

      showToastMessage(responseTexts.SERVICE_WORKFLOW_START_SUCCESS, 'success');
      fetchServiceTaskDetails();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_SERVICE_WORKFLOW, 'error');
    }
  };

  const upsertServiceNote = async (
    service: ServiceTaskDetails,
    draft: ServiceDraft,
  ): Promise<void> => {
    const note = draft.note.trim();
    if (!note) {
      return;
    }

    const response = await Axios.post(ApiEndpoints.SERVICE_PROCESS_UPSERT_NOTES, {
      orderId: orderDetails?.orderId,
      orderSpeciesId: orderDetails?.orderSpeciesId,
      orderServiceId: service.orderServiceId,
      note,
      ...(draft.noteId ? { id: draft.noteId } : {}),
    });

    const updatedNote = response?.data?.data;
    if (updatedNote?.id) {
      updateDraft(service.orderServiceId, { noteId: updatedNote.id });
    }
  };

  const handleCompleteProcessing = async (
    service: ServiceTaskDetails,
  ): Promise<void> => {
    const draft = serviceDrafts[service.orderServiceId] ?? buildDefaultDraft(service);

    try {
      await Axios.post(ApiEndpoints.SERVICE_PROCESS_WORKFLOW, {
        orderServiceId: service.orderServiceId,
        status: OrderProcessStatus.COMPLETED,
        finalMeasurement: draft.measurement ?? 0,
      });

      await upsertServiceNote(service, draft);

      showToastMessage(responseTexts.SERVICE_WORKFLOW_FINISH_SUCCESS, 'success');
      setDirectCompleteServiceId(null);
      setEditingServiceId(null);
      fetchServiceTaskDetails();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_SERVICE_WORKFLOW, 'error');
    }
  };

  const handleCancelInlineEdit = (service: ServiceTaskDetails): void => {
    updateDraft(service.orderServiceId, buildDefaultDraft(service));
    setDirectCompleteServiceId(null);
    setEditingServiceId(null);
  };

  const handleIncrement = (service: ServiceTaskDetails): void => {
    const current =
      serviceDrafts[service.orderServiceId]?.measurement ??
      service.serviceProcessDetail?.finalMeasurement ??
      0;

    updateDraft(service.orderServiceId, { measurement: current + 0.5 });
  };

  const handleDecrement = (service: ServiceTaskDetails): void => {
    const current =
      serviceDrafts[service.orderServiceId]?.measurement ??
      service.serviceProcessDetail?.finalMeasurement ??
      0;

    updateDraft(service.orderServiceId, {
      measurement: current > 0 ? current - 0.5 : 0,
    });
  };

  /**
   * Props object for configuring the breadcrumb component in the service processing view.
   *
   * @property {Array} steps - The list of breadcrumb steps to display.
   *   Typically imported from `ServiceProcessingCrumbSteps`.
   * @property {string} activeItem - The key or name of the currently active breadcrumb step.
   * @property {string | number} orderNumber - The order number associated with the service.
   * @property {string} speciesName - The name of the species related to the order.
   * @property {OrderProcessingMasterData} type - The type of order processing being handled.
   */
  const BreadCrumbsProps = {
    steps: ServiceProcessingCrumbSteps,
    activeItem: 'species_name',
    orderNumber: orderNumber,
    speciesName: speciesName,
    type: OrderProcessingMasterData.SERVICE_PROCESSING,
    stateProp: orderDetails,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={4} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-gray-700">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="p-1"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L4.29289 7.70711C3.90237 7.31658 3.90237 6.68342 4.29289 6.29289L9.29289 1.29289C9.68342 0.902369 10.3166 0.902369 10.7071 1.29289C11.0976 1.68342 11.0976 2.31658 10.7071 2.70711L6.41421 7L10.7071 11.2929C11.0976 11.6834 11.0976 12.3166 10.7071 12.7071Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                {speciesName}{' '}
                {orderDetails?.orderCheckinInches &&
                  orderDetails.orderCheckinFeet && (
                    <>
                      {''} ({orderDetails.orderCheckinFeet} {Length.FEET}{' '}
                      {orderDetails.orderCheckinInches} {Length.INCHES})
                    </>
                  )}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setShowNotesModal(true)}
              className="text-primary p-2 text-sm font-bold"
            >
              View Notes
            </button>
          </div>
          {/* Search and title */}
          <div className="flex p-1 items-center justify-between">
            <h2 className="text-xl font-semibold">Service</h2>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="border p-1 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded-md"
              />
              <div className="absolute inset-y-0 right-2 flex items-center">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="text-gray-500 text-sm"
                />
              </div>
            </div>
          </div>
          {/* Order Notes */}

          {showNotesModal && (
            <OrderNotesModal
              speciesName={orderDetails?.speciesTag ?? ''}
              onClose={() => setShowNotesModal(false)}
              checkInNotes={
                orderDetails?.orderCheckinNotes != ''
                  ? (orderDetails?.orderCheckinNotes ?? '')
                  : errorMessage.NO_NOTES_FOUND
              }
              AdditionalNotes={
                orderDetails?.orderSpeciesNotes != ''
                  ? (orderDetails?.orderSpeciesNotes ?? '')
                  : errorMessage.NO_NOTES_FOUND
              }
            />
          )}

          {orderDetails?.services?.map((service) => {
            const isInitial =
              service.orderServiceStatus === OrderProcessStatus.INITIAL;
            const isInProgress =
              service.orderServiceStatus === OrderProcessStatus.INPROGRESS;
            const isCompleted =
              service.orderServiceStatus >= OrderProcessStatus.COMPLETED;
            const isDirectComplete =
              directCompleteServiceId === service.orderServiceId;
            const isEditingCompleted =
              editingServiceId === service.orderServiceId;
            const showInlineEditor =
              isInProgress || isDirectComplete || isEditingCompleted;

            const draft =
              serviceDrafts[service.orderServiceId] ?? buildDefaultDraft(service);

            return (
              <div
                key={service.orderServiceId}
                className={`rounded-md border shadow-md mt-4 ${showInlineEditor ? 'bg-quaternary' : 'bg-[#FFFFFF]'
                  }`}
              >
                <div>
                  <div className="p-3 rounded-md flex items-center gap-4">
                    <img
                      src={orderDetails.imageUrl}
                      alt={orderDetails.speciesTag}
                      className="w-20 h-20 object-cover rounded-md"
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-lg">{service.serviceName}</p>
                        <div className="flex items-center gap-2">
                          {isCompleted && !isEditingCompleted && (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  ensureDraft(service);
                                  setEditingServiceId(service.orderServiceId);
                                }}
                                className="font-bold text-primary flex items-center gap-2"
                              >
                                <FontAwesomeIcon icon={faPen} />
                                Edit
                              </button>
                              <span className="text-[#00000033] text-2xl">|</span>
                            </>
                          )}
                          <OrderStatusBadge
                            status={service.orderServiceStatus}
                            type={'serviceStatus'}
                          />
                        </div>
                      </div>
                      <div className="bg-[#F5F5F5] w-full p-2 rounded-md text-sm inline-block">
                        {service.serviceFlatFeeFlag
                          ? 'Flat Fee'
                          : 'Final Price to be calculated after processing'}
                      </div>

                      {showInlineEditor && !service.serviceFlatFeeFlag && (
                        <div className="bg-[#FFFFFF] p-2 rounded-md flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                          <span>Measurements after processing</span>
                          <div className="flex items-center">
                            <button
                              type="button"
                              onClick={() => handleDecrement(service)}
                              className="w-12 h-8 flex items-center justify-center border rounded-l text-white text-lg bg-primary hover:bg-primary"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={draft.measurement}
                              onChange={(e) =>
                                updateDraft(service.orderServiceId, {
                                  measurement: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-20 h-8 text-center border-t border-b focus:outline-primary"
                            />
                            <button
                              type="button"
                              onClick={() => handleIncrement(service)}
                              className="w-12 h-8 flex items-center justify-center border rounded-r text-white text-lg bg-primary hover:bg-primary"
                            >
                              +
                            </button>
                            <span className="ml-2">{service.serviceUnit}</span>
                          </div>
                        </div>
                      )}

                      {showInlineEditor && (
                        <div className="bg-[#FFFFFF] p-2 rounded-md">
                          <label className="text-sm font-medium text-gray-700 block mb-1">
                            Service notes
                          </label>
                          <textarea
                            value={draft.note}
                            onChange={(e) =>
                              updateDraft(service.orderServiceId, {
                                note: e.target.value,
                              })
                            }
                            className="w-full border rounded p-2 min-h-[90px] resize-none focus:outline-primary"
                            placeholder="Enter service notes..."
                          />
                        </div>
                      )}

                      {isCompleted && !isEditingCompleted && (
                        <>
                          {!service.serviceFlatFeeFlag && (
                            <div className="bg-[#F5F5F5] w-full p-2 rounded-md text-sm inline-block">
                              {service.serviceProcessDetail?.finalMeasurement
                                ? `Final measurements: ${service.serviceProcessDetail.finalMeasurement} ${service.serviceUnit}`
                                : 'Final Price to be calculated after processing'}
                            </div>
                          )}
                          <div className="bg-[#F5F5F5] w-full p-2 rounded-md text-sm inline-block break-words">
                            {service.serviceNotes?.[0]?.note || errorMessage.NO_NOTES_FOUND}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {service.serviceProcessDetail?.serviceStartDate && (
                    <div className="px-3 py-1 flex justify-between">
                      <span className="text-gray-600 font-semibold">
                        Start date:{' '}
                        {new Date(
                          service.serviceProcessDetail.serviceStartDate,
                        ).toLocaleString('en-US')}
                      </span>
                      {service.serviceProcessDetail?.serviceEndDate && (
                        <span className="text-gray-600 font-semibold">
                          End date:{' '}
                          {new Date(
                            service.serviceProcessDetail.serviceEndDate,
                          ).toLocaleString('en-US')}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="p-3">
                    {isInitial && !isDirectComplete ? (
                      <div className="flex justify-between gap-4 mt-1">
                        <button
                          type="button"
                          onClick={() => handleStartProcessing(service)}
                          className="w-[50%] px-5 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                        >
                          Start Processing
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            ensureDraft(service);
                            setDirectCompleteServiceId(service.orderServiceId);
                          }}
                          className="w-[50%] px-5 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          Complete Processing
                        </button>
                      </div>
                    ) : isInProgress ? (
                      <button
                        type="button"
                        onClick={() => handleCompleteProcessing(service)}
                        className="w-full mb-3 px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                      >
                        Complete Processing
                      </button>
                    ) : isDirectComplete ? (
                      <div className="flex justify-between gap-4 mt-3">
                        <button
                          onClick={() => handleCancelInlineEdit(service)}
                          type="button"
                          className="w-[50%] px-5 py-2 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCompleteProcessing(service)}
                          className="w-[50%] px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                        >
                          Complete Processing
                        </button>
                      </div>
                    ) : isEditingCompleted ? (
                      <div className="flex justify-between gap-4 mt-3">
                        <button
                          onClick={() => handleCancelInlineEdit(service)}
                          type="button"
                          className="w-[50%] px-5 py-2 border border-primary rounded-md text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCompleteProcessing(service)}
                          className="w-[50%] px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
                        >
                          Save Changes
                        </button>
                      </div>
                    ) : (
                      <span className="inline-block w-full border bg-[#CCEECC] p-2 text-[#2D5B4A] border-[#2D5B4A] text-center font-semibold rounded-md">
                        Service Completed
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="ml-2 text-primary"
                        />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
